/**
 * First Launch Wizard Component
 *
 * Main container for the setup wizard that appears on first launch.
 * Guides users through use-case selection, system check, disk space review,
 * batch installation, and completion.
 */

import { useSetupWizard } from '../../hooks/useSetupWizard';
import { useTranslation } from '../../hooks/useTranslation';
import { WelcomeStep } from './steps/WelcomeStep';
import { UseCaseStep } from './steps/UseCaseStep';
import { SystemCheckStep } from './steps/SystemCheckStep';
import { DiskSpaceStep } from './steps/DiskSpaceStep';
import { InstallationProgressStep } from './steps/InstallationProgressStep';
import { CompletionStep } from './steps/CompletionStep';
import { StepIndicator } from './StepIndicator';

interface FirstLaunchWizardProps {
  onComplete: () => void;
}

export function FirstLaunchWizard({ onComplete }: FirstLaunchWizardProps) {
  const { t } = useTranslation();
  const {
    currentStep,
    steps,
    isLoading,
    error,
    systemCheck,
    isSystemCheckLoading,
    gpuCalibrationRetryCount,
    maxGpuCalibrationRetries,
    selectedUseCases,
    setSelectedUseCases,
    resolvedDependencies,
    isDependencyResolving,
    selectedOptionalComponents,
    toggleOptionalComponent,
    currentStepIndex,
    totalSteps,
    progressPercent,
    goToNextStep,
    goToPreviousStep,
    runSystemCheck,
    cancelInstallation,
    completeSetup,
    skipWizard,
  } = useSetupWizard();

  // Handle complete
  const handleComplete = async () => {
    try {
      await completeSetup();
      onComplete();
    } catch {
      // Error is shown in UI
    }
  };

  // Handle skip
  const handleSkip = async () => {
    await skipWizard();
    onComplete();
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent mx-auto mb-4" />
          <p className="text-white/70">{t('wizard.loading', 'Loading setup wizard...')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black/90 backdrop-blur-xl">
      {/* Header with progress */}
      <div className="flex-shrink-0 border-b border-white/10 px-4 sm:px-6 py-4 bg-black/30">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-xl sm:text-2xl font-bold text-white">
              {t('wizard.title', 'Asgard Studio Setup')}
            </h1>
            <button
              onClick={handleSkip}
              className="text-sm text-white/50 hover:text-white/80 transition-colors"
            >
              {t('wizard.skip', 'Skip Setup')}
            </button>
          </div>

          <StepIndicator
            steps={steps}
            currentStepIndex={currentStepIndex}
            progressPercent={progressPercent}
          />
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="flex-shrink-0 bg-red-900/30 border-b border-red-800/50 px-4 py-3">
          <div className="max-w-3xl mx-auto flex items-center gap-2 text-red-200">
            <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          {currentStep === 'welcome' && <WelcomeStep onNext={goToNextStep} onSkip={handleSkip} />}

          {currentStep === 'use_case_selection' && (
            <UseCaseStep
              selectedUseCases={selectedUseCases}
              onSelectionChange={setSelectedUseCases}
              onNext={goToNextStep}
              onBack={goToPreviousStep}
            />
          )}

          {currentStep === 'system_check' && (
            <SystemCheckStep
              systemCheck={systemCheck}
              isLoading={isSystemCheckLoading}
              gpuCalibrationRetryCount={gpuCalibrationRetryCount}
              maxGpuCalibrationRetries={maxGpuCalibrationRetries}
              onNext={goToNextStep}
              onBack={goToPreviousStep}
              onRecheck={runSystemCheck}
            />
          )}

          {currentStep === 'disk_space' && (
            <DiskSpaceStep
              components={resolvedDependencies?.components ?? []}
              optionalComponents={resolvedDependencies?.optionalComponents ?? []}
              selectedOptionalIds={selectedOptionalComponents}
              onToggleOptional={toggleOptionalComponent}
              totalDiskGb={resolvedDependencies?.totalDiskGb ?? 0}
              availableDiskGb={resolvedDependencies?.availableDiskGb ?? 0}
              sufficient={resolvedDependencies?.sufficient ?? true}
              isLoading={isDependencyResolving}
              onNext={goToNextStep}
              onBack={goToPreviousStep}
              onSkip={goToNextStep}
            />
          )}

          {currentStep === 'installation_progress' && (
            <InstallationProgressStep onNext={goToNextStep} onCancel={cancelInstallation} />
          )}

          {currentStep === 'completion' && (
            <CompletionStep onComplete={handleComplete} onBack={goToPreviousStep} />
          )}
        </div>
      </div>

      {/* Footer with step counter */}
      <div className="flex-shrink-0 border-t border-white/10 px-4 sm:px-6 py-3 bg-black/30">
        <div className="max-w-3xl mx-auto flex items-center justify-center text-sm text-white/40">
          {t('wizard.stepCounter', 'Step {{current}} of {{total}}', {
            current: currentStepIndex + 1,
            total: totalSteps,
          })}
        </div>
      </div>
    </div>
  );
}

export default FirstLaunchWizard;
